<template>
  <div>
    <es-exception
      v-if="!hasModule('DRMCH:SRCH:OPTIONS:PRSN:SRCH')"
      exceptionMessage="Sorry, you are not authorized to access this content."
    />
    <es-loader
      v-else-if="loading"
      style="margin: 1rem"
    />
    <es-exception
      v-else-if="loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
    />
    <div
      v-else
      class="document-container"
    >
      <div class="document gap">
        <div class="header">
          <es-header>
            <template #header>
              <div class="header-title">
                Search Options
              </div>
            </template>
          </es-header>
        </div>
        <div class="container-search">
          <div class="columns is-3-desktop search-form multi-dropdown-columns">
            <div class="column">
              <div class="field">
                <label class="label has-text-weight-bold">
                  Identifier :
                </label>
                <div class="control">
                  <input
                    class="input"
                    type="text"
                    v-model="idInput"
                  >
                </div>
              </div>
            </div>
            <div class="column">
              <es-select
                keyValue="code"
                label="Match Type :"
                ref="selPrograms"
                valueTpl="${description}"
                v-on:update:values="programs = $event"
                :preSelected="programs"
                :setValues="this.indexProgramsNonDCX"
              />
            </div>
            <div class="column">
              <es-select
                api="districts"
                keyValue="code"
                ref="selDistricts"
                searchMinLength="2"
                v-on:update:values="districts = $event"
                :label="`${getFieldLabel(`PER:${personType}:DIRECTMATCH:DISTRICT_CODE`)} :`"
                :preSelected="districtCodes"
                :search="true"
                :valueTplFn="(result) => `${result.location.name} (${result.code})`"
              />
            </div>
            <div class="column">
              <es-select
                api="schools"
                keyValue="code"
                ref="selSchools"
                searchMinLength="2"
                v-on:update:values="schools = $event"
                :apiQueryParams="getDistrictParams"
                :label="`${getFieldLabel(`PER:${personType}:DIRECTMATCH:SCHOOL_CODE`)} :`"
                :preSelected="schoolCodesPromises"
                :search="true"
                :subValueTplFn="(result) =>
                  `${result.districtLocation.name} (<i>${result.districtCode}</i>)`"
                :valueTplFn="(result) => `${result.location.name} (${result.code})`"
              />
            </div>
            <div class="column">
              <es-select
                keyValue="id"
                ref="selYears"
                valueTpl="${year}"
                v-on:update:values="schoolYearInput = $event"
                :label="`${getFieldLabel(`PER:${personType}:DIRECTMATCH:SCHOOL_YEAR`)} :`"
                :preSelected="schoolYearInput"
                :setValues="years"
              />
            </div>
          </div>
        </div>
        <div style="padding: 1rem">
          <div class="field is-grouped flex-justify--end">
            <div class="control is-flex flex-align--center">
              <a @click="resetForm">
                Reset
              </a>
            </div>
            <div class="control">
              <button
                class="button is-primary"
                @click="doSearch"
                :disabled="!hasViewParams"
                :title="!hasViewParams ? 'Match Type is required.' : null"
              >
                Search
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        class="document"
        style="margin-top: 1rem"
      >
        <div class="header">
          <es-header>
            <template #header>
              <div class="header-title">
                <div>
                  Search Results
                </div>
              </div>
            </template>
            <template #actions>
              <ul>
                <li
                  v-if="searchResults.data
                    && searchResults.data.length
                    && hasModule('DRMCH:DWNLD:DOWNLOAD')"
                  class="no-border p-narrow"
                >
                  <div class="list-content">
                    <router-link
                      v-if="districtCodes.length"
                      :to="{
                        name: 'directMatchSearchDownload',
                        query: getDownloadParams,
                        params: { totalCount }
                      }"
                      :class="{ 'disabled': !districtCodes.length }"
                    >
                      <font-awesome-icon
                        :icon="['far', 'file-download']"
                        class="fa-lg"
                      />
                    </router-link>
                    <font-awesome-icon
                      v-else
                      :icon="['far', 'file-download']"
                      class="fa-lg"
                      title="Select a district to download person records."
                      style="color: #999"
                    />
                  </div>
                </li>
              </ul>
            </template>
          </es-header>
        </div>
        <es-exception
          v-if="!hasParams"
          style="padding: 6.5rem"
          margin="0rem"
          exceptionMessage="Match Type must be entered to view results."
        />
        <div class="content">
          <table
            v-if="hasParams"
            class="table is-hoverable is-fullwidth"
          >
            <es-sorter
              :apiSorter="apiSorter"
              :columnHeaders="columnHeaders"
              @updateSorter="updateSorter"
            />
            <tbody v-if="loadingStudents">
              <tr style="border-bottom: none">
                <td
                  colspan="100%"
                  style="text-align: center"
                >
                  <es-loader style="margin: 6.5rem"/>
                </td>
              </tr>
            </tbody>
            <tbody
              v-if="!loadingStudents
                && searchResults.data
                && !searchResults.data.length"
            >
              <tr style="border-bottom: none">
                <td
                  colspan="100%"
                  class="has-text-centered"
                >
                  <div style="padding: 6.5rem">
                    <div class="icon">
                      <font-awesome-icon
                        :icon="['fal', 'exclamation-triangle']"
                        size="4x"
                      />
                    </div>
                    <p></p>
                    <h4>
                      No results were found.
                    </h4>
                  </div>
                </td>
              </tr>
            </tbody>
            <tbody
              v-if="!loadingStudents
                && searchResults
                && searchResults.data"
            >
              <tr
                v-for="student in searchResults.data"
                :key="student.identifier"
              >
                <td class="has-text-weight-bold">
                  {{ formatStateID(student.stateStudentId) }}
                </td>
                <td v-if="hasModule('DRMCH:PRPRF:PROFILE:VIEW')">
                  <router-link
                    :to="{
                      path: personProfilePath(student)
                    }"
                  >
                    {{ student.fullName }}
                  </router-link>
                </td>
                <td v-else>
                  {{ student.fullName }}
                </td>
                <td class="has-text-weight-bold">
                  <span v-if="student.dateOfBirth">
                    {{ student.dateOfBirth | formatDate }}
                  </span>
                </td>
                <td>
                  {{ getGender(student.genderCode) }}
                </td>
                <td>
                  <span v-if="student.gradeCode">
                    {{ getGrade(student.gradeCode) }}
                  </span>
                </td>
                <td>
                  <div class="is-flex flex-direction--column">
                    <div class="">
                      <div v-if="student.district">
                        <router-link
                          :to="{
                            name: 'DmDistrictInfoDetails',
                            params: { code: student.districtCode },
                          }"
                          class="has-text-weight-bold"
                        >
                          {{ student.district.location.name }}
                        </router-link>
                        <span class="has-text-grey">
                          ({{ student.districtCode }})
                        </span>
                      </div>
                      <div v-else>
                        Multiple Districts
                      </div>
                    </div>
                    <div class="is-italic">
                      <div v-if="student.schoolCode">
                        <router-link
                          :to="{
                            name: 'DmSchoolInfoDetails',
                            params: {
                              districtCode: student.districtCode,
                              schoolCode: student.schoolCode
                            },
                          }"
                        >
                          {{ student.school.location.name }}
                        </router-link>
                        <span class="has-text-grey">
                          ({{ student.schoolCode }})
                        </span>
                      </div>
                      <div
                        v-else
                        class="has-text-grey"
                      >
                        Multiple Schools
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <div class="has-text-weight-bold">
                    {{ student.sourceId }}
                  </div>
                  <div class="has-text-grey">
                    {{ student.sourceCode }}
                  </div>
                </td>
                <td>
                  <div class="tags">
                    <div
                      v-for="(program, index) in student.programs"
                      class="tag source-tag"
                      :key="index"
                    >
                      {{ getPrograms(program.code) }}
                    </div>
                  </div>
                </td>
                <td class="has-text-weight-bold">
                  <span v-if="student.earliestIdentifiedDate">
                    {{
                      new Intl.DateTimeFormat('en', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                      })
                      .format(new Date(student.earliestIdentifiedDate))
                    }}
                  </span>
                </td>
                <td
                  v-if="hasModule('DRMCH:PRPRF:PROFILE:VIEW')"
                  style="padding-left: 30px"
                >
                  <router-link
                    :to="{
                      path: personProfileDrillIn(student)
                    }"
                  >
                    <font-awesome-icon
                      :icon="['far', 'angle-double-right']"
                      class="fa-2x"
                      title="View Details"
                    />
                  </router-link>
                </td>
              </tr>
            </tbody>
            <tfoot
              v-if="!loadingStudents
                && searchResults
                && searchResults.data.length"
            >
              <tr>
                <td colspan="100%">
                  <es-pager
                    :pageSize="pageSize"
                    :totalCount="searchResults.totalCount"
                  />
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import qs from 'qs';
import { mapActions, mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleRight,
  faFileDownload,
} from '@fortawesome/pro-regular-svg-icons';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsPager from '@/components/EsPager.vue';
import EsSelect from '@/components/EsSelect.vue';
import EsSorter from '@/components/EsSorter.vue';
import EsException from '@/components/EsException.vue';
import moment from 'moment';
import BreadcrumbsManager from '@/mixins/BreadcrumbManager';
import FieldLabels from '@/mixins/FieldLabels';
import StateIdUtils from '@/mixins/StateIdUtils';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faAngleDoubleRight,
  faFileDownload,
);

export default {
  mixins: [
    BreadcrumbsManager,
    FieldLabels,
    PermissionManager,
    StateIdUtils,
  ],
  props: {
    id: {
      type: String,
    },
    districtCodes: {
      type: Array,
    },
    page: {
      type: String,
    },
    programCodes: {
      type: Array,
    },
    schoolCodes: {
      type: Array,
    },
    schoolYear: {
      type: String,
    },
    sortField: {
      type: String,
      default: 'name',
    },
    sortOrder: {
      type: String,
      default: 'ASC',
    },
  },
  data() {
    return {
      idInput: this.id,
      districts: this.districtCodes.map(d => ({ code: d })),
      pageSize: 25,
      programs: [],
      schools: this.schoolCodes.map(code => ({ districtCode: code.split(/[:]+/)[1], code: code.split(/[:]+/)[0] })),
      schoolYearInput: null,
      years: [],
      loading: true,
      loadingError: null,
      loadingStudents: false,
      searchResults: [],
      apiSorter: {},
      columnHeaders: [],
      totalCount: null,
      userPersonTypesCodes: null,
    };
  },
  components: {
    EsHeader,
    EsLoader,
    EsPager,
    EsSelect,
    EsSorter,
    EsException,
  },
  async created() {
    this.setBreadcrumb([
      { text: 'Home', to: { name: 'directMatch' } },
      { text: 'Search Options', to: {} },
    ]);
    try {
      await Promise.all([
        this.loadPrograms(),
        this.loadGrades(),
        this.loadDmSchoolYears(),
        this.loadPersonTypes(),
      ]);
      this.programs = this.indexProgramsNonDCX
        .filter(({ code }) => this.programCodes.includes(code));
      this.years = this.dmSchoolYearsDesc;
      this.schoolYearInput = this.years.filter(y => this.schoolYear === y.year);
    } catch (e) {
      this.loadingError = true;
    } finally {
      this.loading = false;
    }
    if (this.hasParams) {
      this.updateStudents();
    }
    this.userPersonTypesCodes = this.userPersonTypes
      .map(item => item.code);
    this.columnHeaders = [
      {
        header: `${this.getFieldLabel(`PER:${this.personType}:DIRECTMATCH:STATE_STUDENT_ID`)}`,
        field: null,
      },
      {
        header: 'Name',
        field: 'name',
        order: null,
        sortable: true,
      },
      {
        header: `${this.getFieldLabel(`PER:${this.personType}:DIRECTMATCH:DATE_OF_BIRTH`)}`,
        field: 'lastName',
      },
      {
        header: `${this.getFieldLabel(`PER:${this.personType}:DIRECTMATCH:GENDER_CODE`)}`,
        field: 'lastAccessedDate',
      },
      {
        header: `${this.getFieldLabel(`PER:${this.personType}:DIRECTMATCH:GRADE_CODE`)}`,
        field: 'gradeCode',
        sortable: true,
      },
      {
        header: 'Location Info',
        field: null,
      },
      {
        header: 'ID Info',
        field: null,
      },
      {
        header: 'Eligibility Types',
        field: null,
      },
      {
        header: 'Earliest Identified Date',
        field: null,
      },
    ];
    if (this.hasModule('DRMCH:PRPRF:PROFILE:VIEW')) {
      this.columnHeaders.push({
        header: 'Actions',
        field: null,
      });
    }
  },
  watch: {
    $route() {
      if (this.hasParams) {
        this.searchResults = [];
        this.updateStudents();
      }
    },
  },
  computed: {
    ...mapGetters(['userPersonTypes']),
    ...mapGetters([
      'user',
      'userDistricts',
      'userSchools',
      'isStateUser',
    ]),
    ...mapGetters('lookups', [
      'activeGrades',
      'indexProgramsNonDCX',
      'dmSchoolYearsDesc',
      'defaultPersonType',
    ]),
    // eslint-disable-next-line vue/no-async-in-computed-properties
    async schoolCodesPromises() {
      const promises = [];
      this.schoolCodes.forEach(code => promises.push(axios.get(`districts/${code.split(/[:]+/)[1]}/schools/${code.split(/[:]+/)[0]}`)));
      // eslint-disable-next-line vue/no-async-in-computed-properties
      const responses = await Promise.all(promises);
      return responses.map(r => r.data);
    },
    getDistrictParams() {
      const params = {};
      params.districtCode = this.districts.map(d => d.code);

      return params;
    },
    hasParams() {
      return this.programCodes.length;
    },
    hasViewParams() {
      return this.programs.length;
    },
    personType() {
      if (this.userPersonTypesCodes.includes(this.defaultPersonType)) {
        return this.defaultPersonType;
      } return this.userPersonTypesCodes[0];
    },
    urlParams() {
      const params = {};

      params.pageNum = this.page ? this.page : 1;
      params.pageSize = this.pageSize;
      params.id = this.id ? this.id : undefined;
      params.programCode = this.programCodes.length ? this.programCodes : undefined;
      params.districtCode = this.districtCodes.length ? this.districtCodes : undefined;
      params.schoolCode = this.schoolCodes.length ? this.getSchoolCodes() : undefined;
      params.schoolYear = this.schoolYear ? this.schoolYear : undefined;

      params.sort = JSON.stringify([{
        field: this.sortField,
        order: this.sortOrder,
      }]);

      return params;
    },
    getDownloadParams() {
      const params = {};

      params.pageNum = this.page ? this.page : undefined;
      params.id = this.id ? this.id : undefined;
      params.programCode = this.programCodes.length ? this.programCodes : undefined;
      params.districtCode = this.districtCodes.length ? this.districtCodes : undefined;
      params.schoolCode = this.schoolCodes.length ? this.schoolCodes : undefined;
      params.schoolYear = this.schoolYear ? this.schoolYear : undefined;

      return params;
    },
    searchParams() {
      const params = {};

      if (this.idInput) {
        params.id = this.idInput.trim();
      }

      if (this.programs.length) {
        params.programCode = this.programs.map(p => p.code);
      }

      if (this.districts.length) {
        params.districtCode = this.districts.map(d => d.code);
      }
      if (this.schools.length) {
        params.schoolCode = this.schools.map(val => `${val.code}:${val.districtCode}`);
      }
      if (this.schoolYearInput.length) {
        params.schoolYear = this.schoolYearInput[0].year;
      }

      if (this.getSorter) {
        params.sortField = this.getSorter.field;
        params.sortOrder = this.getSorter.order;
      }
      return params;
    },
    getSorter() {
      return this.columnHeaders.find(header => header.order);
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadPrograms',
      'loadGrades',
      'loadDmSchoolYears',
      'loadPersonTypes',
    ]),
    updateSorter() {
      this.$router.replace({ query: this.searchParams });
    },
    personProfilePath(student) {
      const retiredFlag = student.retired ? '?retired=true' : '';
      return student.stateStudentId
        ? `personProfile/${student.stateStudentId}/overview${retiredFlag}`
        : `studentIdentifier/${student.identifier}/directMatch${retiredFlag}`;
    },
    personProfileDrillIn(student) {
      const retiredFlag = student.retired ? '?retired=true' : '';
      return student.stateStudentId
        ? `personProfile/${student.stateStudentId}/directMatch${retiredFlag}`
        : `studentIdentifier/${student.identifier}/directMatch${retiredFlag}`;
    },
    doSearch() {
      if (this.hasViewParams) {
        this.$router.replace({ query: this.searchParams });
      }
    },
    async updateStudents() {
      try {
        this.loadingStudents = true;
        const response = await this.getStudents();
        [this.apiSorter] = response.data.sorters;
        this.searchResults = response.data;
        this.totalCount = this.searchResults.totalCount;
      } catch (e) {
        // :TODO call error funciton
      } finally {
        this.loadingStudents = false;
      }
    },
    getStudents() {
      return axios.get('directmatch/search',
        {
          params: this.urlParams,
          paramsSerializer(params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
        });
    },
    getGender(gender) {
      if (gender === 'M') {
        return 'Male';
      }
      return 'Female';
    },
    getSchoolCodes() {
      return this.schoolCodes.map(code => code.split(/[:]+/)[0]);
    },
    getGrade(gradeCode) {
      if (gradeCode) {
        const grades = this.activeGrades.find(grade => grade.code === gradeCode);
        if (grades) {
          return grades.description;
        }
      } return null;
    },
    getPrograms(programCode) {
      if (programCode) {
        const programs = this.indexProgramsNonDCX.find(p => p.code === programCode);
        if (programs) {
          return programs.description;
        }
      } return null;
    },
    resetForm() {
      const { searchParams } = this;
      if (Object.keys(searchParams).length >= 1) {
        this.$router.replace({ query: {} });
      }
      // if user has access to one district then leave that district selected.
      if (this.userDistricts.length !== 1) {
        this.$refs.selDistricts.clear();
      }
      // if user has access to one school then leave that school selected.
      if (this.userSchools.length !== 1) {
        this.$refs.selSchools.clear();
      }
      this.$refs.selPrograms.clear();
      this.$refs.selYears.clear();
      this.idInput = '';
      this.searchResults = [];
      // reset year to CEP-CEP_CURRENT_SCHOOL_YEAR
      this.$refs.selYears.pushValues(this.years
        .filter(y => this.$store.state.propertiesModule.properties['RULE-CURRENT_SCHOOL_YEAR'] === y.year));
    },
    toggleLoading() {
      this.loadingStudents = !this.loadingStudents;
    },
  },
  filters: {
    formatDate(value) {
      if (value) {
        return moment(value).format('MM/DD/YYYY');
      }
      return null;
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "@/sass/custom.sass"
  @import "~bulma/sass/utilities/mixins"

  /deep/ .multi-dropdown-columns.columns
    .field
      .select
        width: 100%
        select
          width: 100%
    .column
      padding-right: 0
      .field
        padding-right: $column-gap
        border-right: 1px dashed #ccc
        @include until($tablet)
          border-right: none
    .column:last-child
      .field
        border-right: none

  .container-search
    padding: 1rem

  div.buttons
    margin-top: 10px
  .source-tag
    background-color: #f8cbad !important
    color: #666

  .padding--top
    padding-top: 250px
</style>
